"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
} from "@mui/icons-material"

import PageTransition from "../components/PageTransition"
import QueryInput from "../components/QueryInput"
import { API_BASE } from "../config/api"
import ConversationView from "../components/ConversationView"
import FloatingActionButton from "../components/FloatingActionButton"

const PageContainer = styled(PageTransition)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const PageTitle = styled.h1`
  font-size: 2rem;
  color: var(--primary-text);
  display: flex;
  align-items: center;
  gap: 12px;
`

const TitleIcon = styled(motion.div)`
  background: linear-gradient(135deg, var(--accent-blue) 0%, var(--highlight) 100%);
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`

const ActionButton = styled(motion.button)`
  background-color: rgba(79, 141, 251, 0.1);
  border: 1px solid rgba(79, 141, 251, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  color: var(--primary-text);
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(79, 141, 251, 0.2);
    border-color: var(--accent-blue);
  }
`

const QueryPage = () => {
  const [conversation, setConversation] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [progressMessages, setProgressMessages] = useState([])
  const navigate = useNavigate()

  const handleSubmit = (query) => {
    if (!query.trim()) return

    const newConversationItem = {
      query: query.trim(),
      result: null,
    }

    setConversation([...conversation, newConversationItem])
    setIsLoading(true)
    setProgressMessages([])
  }

  const handleProgress = (progress) => {
    setProgressMessages((prev) => [...prev, progress])
    if (progress.step === "error") {
      toast.error(progress.message, {
        position: "top-right",
        autoClose: 5000,
      })
      setConversation((prev) => prev.slice(0, -1)) // Remove failed query
      setIsLoading(false)
    }
  }

  const handleResult = (result) => {
    setConversation((prev) => {
      const updated = [...prev]
      updated[updated.length - 1].result = {
        output: {
          json: result.output.json_data,
          html: result.output.html,
          raw: result.output.raw,
        },
        visualizationPath: result.visualization_path,
        docxPath: result.docx_path,
      }
      return updated
    })
    setIsLoading(false)
    setProgressMessages([])
    toast.success("Query processed successfully!", {
      position: "top-right",
      autoClose: 3000,
    })
  }

  const handleNewQuery = () => {
    setConversation([])
  }

  const handleViewHistory = () => {
    navigate("/history")
  }

  const handleDownloadReport = () => {
    if (conversation.length > 0) {
      const lastItem = conversation[conversation.length - 1]
      if (lastItem.result && lastItem.result.docxPath) {
        window.open(`${API_BASE}/download/${lastItem.result.docxPath}`, "_blank")
      } else {
        toast.info("No report available to download")
      }
    } else {
      toast.info("No report available to download")
    }
  }

  // Animation variants
  const titleIconVariants = {
    initial: { rotate: -30, scale: 0 },
    animate: {
      rotate: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
      },
    },
  }

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0 6px 16px rgba(79, 141, 251, 0.3)",
    },
    tap: { scale: 0.95 },
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <TitleIcon variants={titleIconVariants} initial="initial" animate="animate">
            <SearchIcon />
          </TitleIcon>
          Synthlinx Query
        </PageTitle>

        <ActionButtons>
          <ActionButton onClick={handleNewQuery} variants={buttonVariants} whileHover="hover" whileTap="tap">
            <RefreshIcon fontSize="small" />
            New Conversation
          </ActionButton>

          <ActionButton onClick={handleViewHistory} variants={buttonVariants} whileHover="hover" whileTap="tap">
            <HistoryIcon fontSize="small" />
            View History
          </ActionButton>
        </ActionButtons>
      </PageHeader>

      <ConversationView
        conversation={conversation}
        isLoading={isLoading}
        progressMessages={progressMessages}
      />

      <QueryInput
        onSubmit={handleSubmit}
        onProgress={handleProgress}
        onResult={handleResult}
        isLoading={isLoading}
      />

      <FloatingActionButton
        onNewQuery={handleNewQuery}
        onViewHistory={handleViewHistory}
        onDownloadReport={handleDownloadReport}
        onShare={() => toast.info("Share functionality coming soon!")}
      />
    </PageContainer>
  )
}

export default QueryPage

